import { defineStore } from "pinia";
import { useNotifications } from "./notifications";

import { FunctionalGroupCode } from "@/types/DocumentTypes";
import { HierarchicalGroup } from "@/types/generated/ExperienceMasterDataApi";
import { AvailableLanguage } from "@/types/Language";
import { GenericHighlight, HighlightsMasterDataKey } from "@/types/Highlights";
import { useLogger } from "@/features/core-shared/composables/useLogger";
import { Country } from "@/types/Country";
import { COUNTRIES } from "@/constants/countries.constants";
import {
  automaticTranslationLanguages as automaticTranslationLanguagesList,
  availableLanguages as availableLanguagesList,
} from "@/features/masterdata/lib/masterdata-languages";
import { useCurrenciesMasterdataQuery } from "@/features/masterdata/queries/currencies-masterdata-query";
import { useGeoCountriesMasterdataQuery } from "@/features/masterdata/queries/geo-countries-masterdata-query";
import { useGeoCitiesMasterdataQuery } from "@/features/masterdata/queries/geo-cities-masterdata-query";
import { useMarketsMasterdataQuery } from "@/features/masterdata/queries/markets-masterdata-query";
import { useExperienceCategoriesMasterdataQuery } from "@/features/masterdata/queries/experience-categories-masterdata-query";
import { useExperienceInterestsMasterdataQuery } from "@/features/masterdata/queries/experience-interests-masterdata-query";
import { useSuppliersMasterdataQuery } from "@/features/masterdata/queries/suppliers-masterdata-query";
import { useHighlightsMasterdataQuery } from "@/features/masterdata/queries/highlights-masterdata-query";
import { useIncludedMasterdataQuery } from "@/features/masterdata/queries/included-masterdata-query";
import { useImportantInformationMasterdataQuery } from "@/features/masterdata/queries/important-information-masterdata-query";
import { useAdditionalServicesMasterdataQuery } from "@/features/masterdata/queries/additional-services-masterdata-query";
import { useHierarchicalGroupsMasterdataQuery } from "@/features/masterdata/queries/hierarchical-groups-masterdata-query";
import { useAllMasterdataQuery } from "@/features/masterdata/queries/all-masterdata-query";

function handleMasterDataError(err: unknown) {
  const notificationStore = useNotifications();
  const { logError } = useLogger();

  logError("master-data", err);
  notificationStore.addNotification({
    message: "notifications.error.fetching.master.data",
    theme: "error",
  });
}

function mapHierarchicalGroupsToMap(groups: HierarchicalGroup[]): Map<string, HierarchicalGroup> {
  const groupMap = new Map<string, HierarchicalGroup>();

  for (const group of groups) {
    groupMap.set(group.code, group);
  }

  return groupMap;
}

/**
 * This store is deprecated. Use direct composables instead:
 * - useStaticMasterdata() for static data (languages, countries)
 * - useGeoCountriesMasterdata() for countries data
 * - useGeoCitiesMasterdata() for cities data
 * - useCurrenciesMasterdata() for currencies
 * - useHighlightsMasterdata() for highlights
 * - etc.
 */
export const useMasterData = defineStore("master-data-store", () => {
  // State
  const availableLanguages = ref<Set<AvailableLanguage>>(availableLanguagesList);
  const automaticTranslationLanguages = ref<Set<AvailableLanguage>>(automaticTranslationLanguagesList);
  const countries = ref<Country[]>(COUNTRIES);

  const currenciesQuery = useCurrenciesMasterdataQuery();
  const currencies = computed(() => currenciesQuery.data.value || []);
  const geoCountriesQuery = useGeoCountriesMasterdataQuery();
  const geoCountries = computed(() => geoCountriesQuery.data.value || []);
  const geoCitiesQuery = useGeoCitiesMasterdataQuery();
  const geoCities = computed(() => geoCitiesQuery.data.value || []);
  const marketsQuery = useMarketsMasterdataQuery();
  const markets = computed(() => marketsQuery.data.value || []);
  const experienceCategoriesQuery = useExperienceCategoriesMasterdataQuery();
  const experienceCategories = computed(() => experienceCategoriesQuery.data.value || []);
  const experienceInterestsQuery = useExperienceInterestsMasterdataQuery();
  const experienceInterests = computed(() => experienceInterestsQuery.data.value || []);
  const suppliersQuery = useSuppliersMasterdataQuery();
  const suppliers = computed(() => suppliersQuery.data.value || []);
  const highlightsQuery = useHighlightsMasterdataQuery();
  const highlights = computed(() => highlightsQuery.data.value || []);
  const includedQuery = useIncludedMasterdataQuery();
  const included = computed(() => includedQuery.data.value || []);
  const importantInformationQuery = useImportantInformationMasterdataQuery();
  const importantInformation = computed(() => importantInformationQuery.data.value || []);
  const additionalServicesQuery = useAdditionalServicesMasterdataQuery();
  const additionalServices = computed(() => additionalServicesQuery.data.value || []);
  const hierarchicalGroupsQuery = useHierarchicalGroupsMasterdataQuery();
  const hierarchicalGroups = computed(() => {
    const groups = hierarchicalGroupsQuery.data.value || [];
    return mapHierarchicalGroupsToMap(groups);
  });

  const allMasterdataQuery = useAllMasterdataQuery();

  // Retro compatibility functions that retrigger queries
  async function getMasterData() {
    try {
      await allMasterdataQuery.refetchAll();
    } catch (error) {
      handleMasterDataError(error);
    }
  }

  // The following functions are kept for retro-compatibility,
  // as they either rely on being part of a store
  // or because they will take too many changes to remove.
  // Please use the composables!

  function getAdditionalServicesByFGCode(code: FunctionalGroupCode) {
    return additionalServices.value.filter((item) => item.functional_group_code === code);
  }

  function getHighlightByCode(key: HighlightsMasterDataKey, code: string) {
    const state = {
      highlights: highlights.value,
      included: included.value,
      importantInformation: importantInformation.value,
    };
    const item = state[key].find((highlight) => highlight.code === code);

    if (item == null) {
      throw new Error(`Could not find highlight of type '${key}' with code ${code} in master data store.`);
    }
    return item as GenericHighlight;
  }

  function getCountryByCode(code: string) {
    const country = geoCountries.value.find((el) => el.iso_code_alpha2.toUpperCase() === code.toUpperCase());
    if (!country) {
      throw new Error(`Could not find any country with code ${code} in master data store.`);
    }
    return country;
  }

  function $reset() {
    // No operation
  }

  return {
    currencies,
    highlights,
    included,
    importantInformation,
    additionalServices,
    availableLanguages,
    automaticTranslationLanguages,
    experienceCategories,
    experienceInterests,
    countries,
    markets,
    geoCountries,
    geoCities,
    hierarchicalGroups,
    suppliers,
    getAdditionalServicesByFGCode,
    getHighlightByCode,
    getCountryByCode,
    getMasterData,
    $reset,
  };
});
